import * as sortBy from 'lodash/sortBy';

const FILTERS_TO_IGNORE_URL = ['less_capacity'];

const isMobileDevice = function () {
  const match = /Android|webOs|iPhone|iPad|BlackBerry|IEMobile|Opera Mini/i.exec(window.navigator.userAgent);
  return match != null;
};

const searchToObject = function (search) {
  return search
    .substring(1)
    .split('&')
    .reduce(function (result, value) {
      const parts = value.split('=');
      if (parts[0]) {
        result[decodeURIComponent(parts[0])] = decodeURIComponent([parts[1]]);
      }
      return result;
    }, {});
};

const priceSort = function(arr, descending = false, prioritizedCategoryIds) {
  let sorted = arr;
  const copy_sorted = [...sorted];
  if (descending) {
    copy_sorted.sort((a, b) => b.min_apartment_price - a.min_apartment_price);
    sorted = copy_sorted.map((item, index) => ({ ...item, index }));
  } else {
    sorted = sortBy(sorted, 'min_apartment_price');
  }
  sorted = sortBy(sorted, (camp) => Math.sqrt((camp.a1 ?? 100000) - 100000) <= 17000 && camp.have_autocompleted_price === true);
  sorted = sortBy(sorted, (camp) => camp.is_contracted === false);
  sorted = categoryPrioritySort(sorted, prioritizedCategoryIds);
  return sorted;
};

const categoryPrioritySort = function (camps, prioritizedCategoryIds) {
  if (!prioritizedCategoryIds?.length) return camps;

  const targetIds = prioritizedCategoryIds;

  return [...camps].sort((a, b) => {
    const superIdsA = a.super_categories_ids || [];
    const superIdsB = b.super_categories_ids || [];
    const mainSuperIdA = superIdsA[superIdsA.length - 1];
    const mainSuperIdB = superIdsB[superIdsB.length - 1];
    const mainMatchA = mainSuperIdA != null && targetIds.includes(mainSuperIdA);
    const mainMatchB = mainSuperIdB != null && targetIds.includes(mainSuperIdB);
    const extraMatchA = superIdsA.some((id) => targetIds.includes(id));
    const extraMatchB = superIdsB.some((id) => targetIds.includes(id));

    const keyA = mainMatchA ? 0 : extraMatchA ? 1 : 2;
    const keyB = mainMatchB ? 0 : extraMatchB ? 1 : 2;
    return keyA - keyB;
  });
};

// Сравнивает тарифы по совпадению количества мест (аналог Services::Tariff::Sort#compare_by_seats).
// В приоритете тариф, у которого разница между занятыми местами и запрошенным количеством гостей
// наименьшая. Если у одного тарифа мест больше, чем требуется, а у другого меньше — приоритет у того,
// у кого больше.
const compareTariffsBySeats = function (a, b, totalGuestsCount) {
  if (totalGuestsCount == null) return 0;

  const aSeats = a?.seats;
  const bSeats = b?.seats;
  const aBlank = aSeats == null;
  const bBlank = bSeats == null;

  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;

  const aDiff = aSeats - totalGuestsCount;
  const bDiff = bSeats - totalGuestsCount;

  if (aDiff * bDiff >= 0) {
    return Math.abs(aDiff) - Math.abs(bDiff);
  }
  return aDiff > 0 ? -1 : 1;
};

const mergeCamps = function (oldCamps, newCamps, totalGuestsCount) {
  const oldCampsIds = oldCamps.map((camp) => camp.id);
  let changed = false;
  newCamps.forEach((new_camp) => {
    const old_camp = oldCamps.find((camp) => camp.id == new_camp.id);
    if (old_camp) {
      changed = true;
      let all_apartments = [...(new_camp.apartments ?? []), ...(old_camp.apartments ?? [])];
      const new_percent = Math.max(new_camp.contract_percent || 0, old_camp.contract_percent || 0);
      const new_a1 = Math.max(new_camp.a1 || 0, old_camp.a1 || 0);
      const new_a2 = Math.max(new_camp.a2 || 0, old_camp.a2 || 0);
      const show_outdated_price = old_camp.show_outdated_price && new_camp.show_outdated_price;
      const have_autocompleted_price = old_camp.have_autocompleted_price || new_camp.have_autocompleted_price; // Иначе ломается сортировка
      old_camp.show_outdated_price = show_outdated_price;
      new_camp.show_outdated_price = show_outdated_price;
      old_camp.have_autocompleted_price = have_autocompleted_price;
      new_camp.have_autocompleted_price = have_autocompleted_price;
      old_camp.contract_percent = new_percent;
      new_camp.contract_percent = new_percent;
      old_camp.a1 = new_a1;
      new_camp.a1 = new_a1;
      new_camp.a2 = new_a2;
      new_camp.a2 = new_a2;
      const has_not_autocompleted = all_apartments.some(a => a.tariffs?.some(t => !t.price_autocompleted));
      if (has_not_autocompleted) {
        all_apartments = all_apartments.map(a => {
          a.tariffs = a.tariffs.filter(t => !t.price_autocompleted);
          return a;
        });
      }
      all_apartments = all_apartments.filter(a => a.tariffs?.length > 0);
      all_apartments.sort((a, b) => {
        const aTariff = a.tariffs[0];
        const bTariff = b.tariffs[0];
        const seatsCompare = compareTariffsBySeats(aTariff, bTariff, totalGuestsCount);
        if (seatsCompare !== 0) return seatsCompare;
        if (aTariff?.instant_booking != bTariff?.instant_booking) {
          return bTariff?.instant_booking * 1 - aTariff?.instant_booking * 1 ;
        }
        return aTariff?.calculated_price - bTariff?.calculated_price
      })
      const new_min_apartment_price = Math.min(new_camp.min_apartment_price || Number.MAX_VALUE, old_camp.min_apartment_price || Number.MAX_VALUE);
      const min_price = Math.min(...all_apartments.flatMap(a => a.tariffs).flatMap(t => t.calculated_price), new_min_apartment_price);
      old_camp.min_apartment_price = min_price;
      new_camp.min_apartment_price = min_price;
      old_camp.apartments = all_apartments;
      new_camp.apartments = all_apartments;
    }
  });

  for (let i = 0; i < oldCamps.length; i++) {
    const oldCamp = oldCamps[i];
    const newCamp = newCamps.find((camp) => camp.id == oldCamp.id);

    if (!newCamp) continue;

    let replace = !oldCamp.is_can_instant_booking && newCamps.is_can_instant_booking;
    replace ||=
      oldCamp.is_can_instant_booking === newCamps.is_can_instant_booking &&
      oldCamp.min_apartment_price > newCamps.min_apartment_price;

    if (replace) {
      oldCamps[i] = newCamp;
    }
  }

  newCamps = newCamps.filter((newCamp) => !oldCampsIds.includes(newCamp.id));

  return { camps: [...oldCamps, ...newCamps], changed };
};

const zIndexForMapMarkerHovered = function () {
  return 50000000;
};

const WITH_PET_TAG = '182';

const zIndexForMapMarker = function (camp, mapCampId, selected = false) {
  let result = 0;
  if (camp.is_recommended === true) result += 1;
  result += Math.sqrt(camp.a2 - 100000) || 0;
  result += (Math.sqrt(camp.a1 - 100000) || 0) / 1000;
  if (camp.is_can_instant_booking == true) result += 10000;
  if (camp.camp_loaded_from_filter_list == true) result += 100000;
  if (camp.min_apartment_price > 0) result += 1000000;
  if (camp.id === mapCampId) result += 10000000;
  if (selected) result += zIndexForMapMarkerHovered();

  return Math.floor(result);
};

const sortForMap = function (camps, mapCampId) {
  let sorted = sortBy(camps, (camp) => camp.is_recommended === true);
  sorted = sortBy(sorted, (camp) => -Math.sqrt(camp.a2 - 100000));
  sorted = sortBy(sorted, (camp) => -Math.sqrt(camp.a1 - 100000));
  //sorted = sortBy(sorted, camp => -camp.commission_in_little_period);
  sorted = sortBy(sorted, (camp) => camp.is_can_instant_booking == true);
  sorted = sortBy(sorted, (camp) => camp.camp_loaded_from_filter_list == true);
  sorted = sortBy(sorted, (camp) => camp.min_apartment_price > 0);
  sorted = sortBy(sorted, (camp) => camp.id === mapCampId);

  sorted.reverse();

  return sorted;
};

const parseUrl = function () {
  // Например при переходе на предыдущую страницу
  if (typeof history === 'undefined' || history === null || !window.location) {
    return;
  }
  const obj = searchToObject(window.location.search);
  if (obj['prices'] != null) { obj.prices = obj.prices.split(',').map(Number); }
  if (obj['categories'] != null) { obj.categories = obj.categories.split(',').map(Number); }
  if (obj['all_categories'] != null) { obj.all_categories = obj['all_categories'].split(',').map(Number); }
  if (obj['all_enabled_categories'] != null) { obj.all_enabled_categories = obj['all_enabled_categories'].split(',').map(Number); }
  if (obj['camp-service-tags'] != null) { obj.campServiceTags = obj['camp-service-tags'].split(','); }
  if (obj['childcare-tags'] != null) { obj.childcareTags = obj['childcare-tags'].split(','); }
  if (obj['camp_treatment_profiles'] != null) { obj.campTreatmentProfiles = obj['camp_treatment_profiles'].split(','); }
  if (obj['camp-locations'] != null) { obj.campLocations = obj['camp-locations'].split(','); }
  if (obj['camp-promotions'] != null) { obj.campPromotions = obj['camp-promotions'].split(','); }
  if (obj['waters'] != null) { obj.waters = obj.waters.split(',').map(Number); }
  if (obj['shore-distance'] != null) { obj.shoreDistance = parseInt(obj['shore-distance']); }
  if (obj['food-types'] != null) { obj.foodTypes = obj['food-types'].split(',').map(Number); }
  if (obj['stars'] != null) { obj.stars = obj['stars'].split(',').map(Number); }
  if (obj['treatment-profiles-tags'] != null) { obj.campTreatmentFactors = obj['treatment-profiles-tags'].split(','); }
  if (obj['camp_id'] != null) { obj.campId = parseInt(obj['camp_id']); }
  if (obj['sight_id'] != null) { obj.sightId = parseInt(obj['sight_id']); }
  if (obj['instant'] != null) { obj.instant = parseInt(obj['instant']); }
  if (obj['notRequiredPrepayment'] != null) { obj.notRequiredPrepayment = parseInt(obj['notRequiredPrepayment']); }
  if (obj['singleRoom'] != null) { obj.singleRoom = parseInt(obj['singleRoom']); }
  if (obj['recovery'] != null) { obj.recovery = obj['recovery'].split(',').map(Number); }
  if (obj['tags_properties'] != null) { obj.tags_properties = obj['tags_properties'].split(','); }
  if (obj['swimming_pools_properties'] != null) { obj.swimming_pools_properties = obj['swimming_pools_properties'].split(','); }
  if (obj['districts'] != null) { obj.districts = obj['districts'].split(','); }
  if (obj['bath-tags'] != null) { obj.bathTags = obj['bath-tags'].split(','); }
  if (obj['isDescending'] === 'false') {
    obj.isDescending = false;
  } else if (obj['isDescending'] === 'true') {
    obj.isDescending = true;
  }
  obj.listShowMode = !window.location.hash.includes('#map_opened');

  for (let filter in obj) {
    if (obj[filter] == null || obj[filter].length == 0) {
      obj[filter] = false;
    }
  }

  return obj;
};

const objToFilters = function (obj) {
  const filters = {};
  if (obj.categories != null) { filters.categories = obj.categories; }
  if (obj.all_enabled_categories != null) { filters.all_enabled_categories = obj.all_enabled_categories; }
  if (obj.distance != null) { filters.distance = parseInt(obj.distance); }
  if (obj.distanceToSkiLift != null) { filters.distanceToSkiLift = parseInt(obj.distanceToSkiLift); }
  if (obj.waters != null) { filters.waters = obj.waters; }
  if (obj.name != null) { filters.name = obj.name; }
  if (obj.campServiceTags != null) { filters.campServiceTags = obj.campServiceTags; }
  if (obj.childcareTags != null) { filters.childcareTags = obj.childcareTags; }
  if (obj.campTreatmentProfiles != null) { filters.campTreatmentProfiles = obj.campTreatmentProfiles; }
  if (obj.campLocations != null) { filters.campLocations = obj.campLocations; }
  if (obj.campPromotions != null) { filters.campPromotions = obj.campPromotions; }
  if (obj.prices != null) { filters.prices = obj.prices; }
  if (obj.shoreDistance != null) { filters.shoreDistance = obj.shoreDistance; }
  if (obj.foodTypes != null) { filters.foodTypes = obj.foodTypes; }
  if (obj.stars != null) { filters.stars = obj.stars; }
  if (obj.instant != null) { filters.instant = obj.instant; }
  if (obj.notRequiredPrepayment != null) { filters.notRequiredPrepayment = obj.notRequiredPrepayment; }
  if (obj.singleRoom != null) { filters.singleRoom = obj.singleRoom; }
  if (obj.campTreatmentFactors != null) { filters.campTreatmentFactors = obj.campTreatmentFactors; }
  if (obj['recovery'] != null) { obj.recovery = obj['recovery'].split(',').map(Number); }
  if (obj.tags_properties != null) { filters.tags_properties = obj.tags_properties; }
  if (obj.swimming_pools_properties != null) { filters.swimming_pools_properties = obj.swimming_pools_properties.map(Number); }
  if (obj.districts != null) { filters.districts = obj.districts; }
  if (obj.bathTags != null) { filters.bathTags = obj.bathTags; }
  return filters;
};

const filtersToQuery = function (filters, pageNum, listShowMode, s_params, sortType, isDescending) {
  const bathTagIds = [142, 141, 140, 189, 2015, 1998].map(String);
  filters.campServiceTags = Array.isArray(filters.campServiceTags)
    ? filters.campServiceTags?.filter((tag) => !bathTagIds.includes(tag))
    : false;
  let filter;
  let object = {};

  let string = '';

  for (filter in filters) {
    if (filters[filter] && !FILTERS_TO_IGNORE_URL.includes(filter)) {
      if (filters[filter] instanceof Array && filters[filter].length > 0) {
        object[filter] = filters[filter].join(',');
      } else if (filters[filter].toString() !== '') {
        object[filter] = filters[filter];
      }
    }
  }

  object['page'] = pageNum + 1;
  object['sortType'] = sortType;
  object['isDescending'] = isDescending;

  for (filter in object) {
    if (!FILTERS_TO_IGNORE_URL.includes(filter)) {
      const value = object[filter];
      string += string.length > 2 ? '&' : '';
      string += convertFilterToSearchParam(filter, value);
    }
  }

  const { count_guests, date_end, date_start, children_ages, with_pet } = s_params || {};
  if (count_guests && count_guests !== 2) string += `&count_guests=${count_guests}`;
  if (date_end) string += `&date_end=${date_end}`;
  if (date_start) string += `&date_start=${date_start}`;
  if (with_pet) string += '&with_pet=true';
  if (Array.isArray(children_ages) && children_ages.length > 0) string += `&children_ages=${children_ages.join()}`;

  if (string[0] === '&') string = string.slice(1);

  if (!listShowMode) {
    string += '#map_opened';
  }

  return { string: string, object: object };
};

const updateUrl = function (filters, pageNum, listShowMode, s_params, sortType, isDescending, seoMapOpened = false) {
  const oldString = window.location.search + window.location.hash;

  let { string, object } = filtersToQuery(filters, pageNum, listShowMode, s_params, sortType, isDescending);

  string = string === '' ? window.location.pathname : `${window.location.pathname}?${string}`;

  let prevHash = oldString.split('#')[1];

  if (string === oldString) return;

  if (prevHash?.length > 0 && !prevHash.includes('map_opened')) {
    // Убираем лишний hash
    if (string.includes('#map_opened')) {
      string = string.replace('#map_opened', '');
    }

    object.string = string;
    history.replaceState(object, document.title, `${string}#${prevHash}`);
  } else {
    // Исключаем #map_opened до тех пор пока карта не будет закрыта и открыта вновь для SEO-страниц с картой
    if (seoMapOpened && string.includes('#map_opened') && !prevHash?.length) {
      string = string.replace('#map_opened', '');
    }

    object.string = string;
    history.replaceState(object, document.title, string);
  }
};

/**
 * Преобразует значение фильтра в параметр URL строки
 *
 * @param {string} filter поле объекта фильтра
 * @param {any} value значение фильтра
 * @return {string} часть строки параметров URL
 */
function convertFilterToSearchParam(filter, value) {
  if (filter === 'shoreDistance') {
    filter = 'shore-distance';
  }
  if (filter === 'campServiceTags') {
    filter = 'camp-service-tags';
  }
  if (filter === 'campTreatmentFactors') {
    filter = 'treatment-profiles-tags';
  }
  if (filter === 'childcareTags') {
    filter = 'childcare-tags';
  }
  if (filter === 'campTreatmentProfiles') {
    filter = 'camp_treatment_profiles_filter';
  }
  if (filter === 'campLocations') {
    filter = 'camp-locations';
  }
  if (filter === 'campPromotions') {
    filter = 'camp-promotions';
  }
  if (filter === 'foodTypes') {
    filter = 'food-types';
  }
  if (filter === 'bathTags') {
    filter = 'bath-tags';
  }

  return `${filter}=${value}`;
}

/**
 * Посчитать количество выбранных фильтров
 *
 * @param filters
 * @param areaCenterDefaultDistance
 * @returns {int} количество выбранных фильтров
 */
const filtersCount = function (filters, areaCenterDefaultDistance, price_max_end) {
  let filtersCount = filters.categories?.length ?? 0;
  filtersCount += filters.all_enabled_categories?.length ?? 0;
  filtersCount += filters.foodTypes?.length ?? 0;
  filtersCount += filters.shoreDistance?.length ?? 0;
  filtersCount += filters.campLocations?.length ?? 0;
  filtersCount += filters.campPromotions?.length ?? 0;
  filtersCount += filters.campServiceTags?.length ?? 0;
  filtersCount += filters.childcareTags?.length ?? 0;
  filtersCount += filters.prices[0] > 0 || filters.prices[1] < price_max_end ? 1 : 0;
  filtersCount += filters.waters?.length ?? 0;
  filtersCount += filters.campTreatmentProfiles?.length ?? 0;
  filtersCount += filters.campTreatmentFactors?.length ?? 0;
  filtersCount += filters.instant?.length ?? 0;
  filtersCount += filters.notRequiredPrepayment?.length ?? 0;
  filtersCount += filters.stars?.length ?? 0;
  filtersCount += filters.tags_properties?.length ?? 0;
  filtersCount += filters.swimming_pools_properties?.length ?? 0;
  filtersCount += filters.districts?.length ?? 0;
  filtersCount += filters.bathTags?.length ?? 0;
  filtersCount += Array.isArray(filters.discount) ? filters.discount.length : Number(filters.discount ?? 0);


  let shoreDistance = filters.shoreDistance;
  filtersCount += shoreDistance === false || shoreDistance.length === 0 || shoreDistance === 0 ? 0 : 1;

  const areaCenterNotDefault = filters.distance && filters.distance !== areaCenterDefaultDistance;
  filtersCount += areaCenterNotDefault ? 1 : 0;

  return filtersCount;
};

const updateStateFilter = function (state, filters) {
  return Object.keys(state).reduce((acc, key) => {
    if (FILTERS_TO_IGNORE_URL.includes(key)) {
      acc[key] = state[key];
      return acc;
    }
    const value = filters[key];
    if (value !== undefined && !(Array.isArray(value) && value.length === 0)) {
      acc[key] = value;
    } else {
      acc[key] = false;
    }
    return acc;
  }, {});
};
export {
  WITH_PET_TAG,
  filtersCount,
  filtersToQuery,
  isMobileDevice,
  mergeCamps,
  objToFilters,
  parseUrl,
  searchToObject,
  sortForMap,
  updateStateFilter,
  updateUrl,
  zIndexForMapMarker,
  zIndexForMapMarkerHovered,
  priceSort
};
